/**
 * Pure filter/search/pivot logic for artifact exploration (docs/specs/app.md, "Artifact
 * exploration (v1.1)"). No React, no DOM, no `window` — URL (de)serialization lives in
 * router.ts; this module only turns a FilterState + Artifact into match/visibility decisions,
 * so it's unit-testable without mounting anything and cheap to re-run per keystroke even on
 * the stress fixture (evals/fixtures/compare/artifact.sample.json: 4 vendors, up to 125
 * features each).
 */
import type {
  Artifact,
  ArtifactDegraded,
  ArtifactDroppedEntry,
  ArtifactFeature,
  ArtifactProposedEntry,
  ArtifactSubfeature,
  ArtifactVendor,
  Axis,
  ComparisonMatrix,
  Verdict,
} from '../types.ts';
import { cellFor, resolveCellEntry } from './artifact.ts';

export type Pivot = 'matrix' | 'vendor';
/** `third_party_evidence` is crawl-2's facet: features any part of whose evidence came from
 * outside the vendor's own site. Spelled with underscores like its neighbours — it is a URL
 * parameter value, and one spelling convention beats matching the spec's prose. */
export type FeatureFacet =
  | 'all'
  | 'compared'
  | 'not_compared'
  | 'proposed'
  | 'dropped'
  | 'third_party_evidence';

export interface FilterState {
  query: string;
  /** Empty = no verdict constraint (all verdicts pass). */
  verdicts: Verdict[];
  /** Company names; empty = no vendor constraint (all vendors pass). */
  vendors: string[];
  facet: FeatureFacet;
  pivot: Pivot;
}

export const DEFAULT_FILTERS: FilterState = {
  query: '',
  verdicts: [],
  vendors: [],
  facet: 'all',
  pivot: 'matrix',
};

export const VERDICT_ORDER: readonly Verdict[] = ['present', 'present_as_subfeature', 'partial', 'absent', 'unknown'];
const FACET_ORDER: readonly FeatureFacet[] = [
  'all',
  'compared',
  'not_compared',
  'proposed',
  'dropped',
  'third_party_evidence',
];

function isVerdict(s: string): s is Verdict {
  return (VERDICT_ORDER as readonly string[]).includes(s);
}
function isFacet(s: string | null): s is FeatureFacet {
  return s !== null && (FACET_ORDER as readonly string[]).includes(s);
}

/** True when any filter differs from its default — drives the "one-click clear-all" button's enabled state. */
export function isFilterActive(f: FilterState): boolean {
  return f.query.trim() !== '' || f.verdicts.length > 0 || f.vendors.length > 0 || f.facet !== 'all';
}

/** Clears search/verdict/vendor/facet, but leaves `pivot` alone — pivot is a view mode, not a filter (spec: "switching pivots preserves the active filters", implying pivot itself survives a clear). */
export function clearFilters(f: FilterState): FilterState {
  return { ...f, query: '', verdicts: [], vendors: [], facet: 'all' };
}

// ---- URL (de)serialization — pure string<->object, no `window` ----

export function filtersToParams(f: FilterState): URLSearchParams {
  const p = new URLSearchParams();
  if (f.query.trim()) p.set('q', f.query);
  if (f.verdicts.length) p.set('verdict', f.verdicts.join(','));
  // Repeated params, not a comma join: company names may legally contain commas ("Acme, Inc.")
  // and must round-trip through a shared URL. Verdicts are a closed comma-free enum.
  for (const v of f.vendors) p.append('vendor', v);
  if (f.facet !== 'all') p.set('facet', f.facet);
  if (f.pivot !== 'matrix') p.set('pivot', f.pivot);
  return p;
}

/** Inverse of filtersToParams. Unknown/malformed values fall back to defaults rather than throwing — a hand-edited or stale shared link should degrade gracefully, not break the view. */
export function filtersFromParams(params: URLSearchParams): FilterState {
  const verdictParam = params.get('verdict');
  const facetParam = params.get('facet');
  const pivotParam = params.get('pivot');
  return {
    query: params.get('q') ?? '',
    verdicts: verdictParam ? verdictParam.split(',').filter(isVerdict) : [],
    vendors: params.getAll('vendor').filter(Boolean),
    facet: isFacet(facetParam) ? facetParam : 'all',
    pivot: pivotParam === 'vendor' ? 'vendor' : 'matrix',
  };
}

// ---- text matching ----

/** Case-insensitive substring match; an empty query matches everything (the no-filter state). */
export function textMatches(haystack: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return haystack.toLowerCase().includes(q);
}

export function subfeatureMatchesQuery(sub: ArtifactSubfeature, query: string): boolean {
  if (!query.trim()) return true;
  if (textMatches(sub.name, query)) return true;
  return sub.mentions.some((m) => m.description !== null && textMatches(m.description, query));
}

/** Search scope per spec: feature/subfeature names, aliases, and mention text. */
export function featureMatchesQuery(feature: ArtifactFeature, query: string): boolean {
  if (!query.trim()) return true;
  if (textMatches(feature.name, query)) return true;
  if (feature.aliases.some((a) => textMatches(a, query))) return true;
  if (feature.mentions.some((m) => m.description !== null && textMatches(m.description, query))) return true;
  return feature.subfeatures.some((s) => subfeatureMatchesQuery(s, query));
}

// ---- vendor filter ----

export function visibleVendors(artifact: Artifact, filters: FilterState): ArtifactVendor[] {
  if (!filters.vendors.length) return artifact.vendors;
  return artifact.vendors.filter((v) => filters.vendors.includes(v.company));
}

/**
 * A failed competitor's column (docs/specs/degraded-runs.md). Deliberately NOT an ArtifactVendor:
 * there is no record behind it, and synthesising an empty one would put a "0 features" card in
 * the vendor pivot — a vendor nobody assessed reading as a vendor that has nothing, which is the
 * harm this whole feature exists to prevent. It is a matrix column and only a matrix column.
 */
export type DegradedColumn = ArtifactDegraded['failed_vendors'][number];

/** The degraded columns the vendor filter lets through. Same rule as visibleVendors, by company. */
export function visibleDegradedColumns(artifact: Artifact, filters: FilterState): DegradedColumn[] {
  const failed = artifact.degraded?.failed_vendors ?? [];
  if (!filters.vendors.length) return failed;
  return failed.filter((v) => filters.vendors.includes(v.company));
}

/** Every company that can head a matrix column — survivors and unassessed alike. */
export function columnCompanies(artifact: Artifact): string[] {
  return [
    ...artifact.vendors.map((v) => v.company),
    ...(artifact.degraded?.failed_vendors ?? []).map((v) => v.company),
  ];
}

// ---- matrix pivot: axis-row visibility ----

/**
 * Whether an axis row should show in the (filtered) matrix pivot. Search matches the axis's
 * own capability name, or — through the matrix cells — the cited feature/subfeature name,
 * aliases, or mention text for any vendor currently in view. Verdict filter matches if any
 * in-view cell's verdict is in the selected set. Both constraints AND together; an empty
 * constraint (no query, no verdict selection) always passes.
 */
export function axisMatchesFilters(
  artifact: Artifact,
  axis: Axis,
  inView: ArtifactVendor[],
  filters: FilterState,
  /** In-view degraded columns. Their cells are read the same way a survivor's are — they are real
   * cells with a real verdict — so a row an unassessed vendor still speaks to cannot be filtered
   * away by a verdict or search constraint the survivors happen not to satisfy. */
  degraded: DegradedColumn[] = [],
): boolean {
  const q = filters.query.trim();
  const degradedCells = degraded
    .map((d) => cellFor(artifact.matrix, axis.capability, d.company))
    .filter((c): c is NonNullable<typeof c> => c !== null);
  const searchOk =
    !q ||
    textMatches(axis.capability, q) ||
    inView.some((v) => {
      const cell = cellFor(artifact.matrix, axis.capability, v.company);
      if (!cell) return false;
      if (cell.note && textMatches(cell.note, q)) return true;
      const resolved = resolveCellEntry(v, cell);
      if (!resolved) return false;
      return resolved.subfeature ? subfeatureMatchesQuery(resolved.subfeature, q) : featureMatchesQuery(resolved.feature, q);
    }) ||
    // an unassessed column carries no record to search, but it does carry why it is unassessed
    degradedCells.some((c) => c.note !== null && textMatches(c.note, q));
  if (!searchOk) return false;

  if (!filters.verdicts.length) return true;
  if (degradedCells.some((c) => filters.verdicts.includes(c.verdict))) return true;
  return inView.some((v) => {
    const cell = cellFor(artifact.matrix, axis.capability, v.company);
    return cell !== null && filters.verdicts.includes(cell.verdict);
  });
}

/** Whether one cell should read as "in the active verdict filter" — used to dim, not hide, cells that don't match while their row still shows because a sibling vendor's cell does. */
export function cellMatchesVerdictFilter(verdict: Verdict, filters: FilterState): boolean {
  return !filters.verdicts.length || filters.verdicts.includes(verdict);
}

// ---- vendor pivot: unified per-vendor list ----

export type VendorListItem =
  | { kind: 'feature'; feature: ArtifactFeature }
  | { kind: 'proposed'; entry: ArtifactProposedEntry }
  | { kind: 'dropped'; entry: ArtifactDroppedEntry };

/** The vendor pivot's "ONE unified searchable feature list" (spec) — compared and not-compared features, plus the totality invariant's proposed/dropped residue, as one list of taggable items. */
export function vendorListItems(vendor: ArtifactVendor): VendorListItem[] {
  return [
    ...vendor.features.map((feature): VendorListItem => ({ kind: 'feature', feature })),
    ...vendor.proposed.map((entry): VendorListItem => ({ kind: 'proposed', entry })),
    ...vendor.dropped.map((entry): VendorListItem => ({ kind: 'dropped', entry })),
  ];
}

export function vendorListItemKey(item: VendorListItem): string {
  switch (item.kind) {
    case 'feature':
      return `feature::${item.feature.product}::${item.feature.name}`;
    case 'proposed':
      return `proposed::${item.entry.product}::${item.entry.name}`;
    case 'dropped':
      return `dropped::${item.entry.product}::${item.entry.name}`;
  }
}

/** Every mention of a feature, its subfeatures' included — what "has third-party evidence" reads. */
export function featureMentions(feature: ArtifactFeature) {
  return [...feature.mentions, ...feature.subfeatures.flatMap((s) => s.mentions)];
}

export function hasThirdPartyEvidence(feature: ArtifactFeature): boolean {
  return featureMentions(feature).some((m) => m.third_party);
}

export function vendorListItemMatchesFacet(item: VendorListItem, facet: FeatureFacet): boolean {
  switch (facet) {
    case 'all':
      return true;
    case 'compared':
      return item.kind === 'feature' && item.feature.compared;
    case 'not_compared':
      return item.kind === 'feature' && !item.feature.compared;
    case 'proposed':
      return item.kind === 'proposed';
    case 'dropped':
      return item.kind === 'dropped';
    // proposed and dropped entries carry no mentions in the artifact, so they can never match
    case 'third_party_evidence':
      return item.kind === 'feature' && hasThirdPartyEvidence(item.feature);
  }
}

export function vendorListItemMatchesQuery(item: VendorListItem, query: string): boolean {
  if (!query.trim()) return true;
  switch (item.kind) {
    case 'feature':
      return featureMatchesQuery(item.feature, query);
    case 'proposed':
      return textMatches(item.entry.name, query) || textMatches(item.entry.product, query);
    case 'dropped':
      return (
        textMatches(item.entry.name, query) ||
        textMatches(item.entry.product, query) ||
        textMatches(item.entry.reason, query)
      );
  }
}

/**
 * The distinct verdicts cited for this feature, via the matrix cells that resolve to it.
 * `feature.cited_by` already includes axes that cite through a subfeature (per its doc comment
 * — "directly, or through one of its subfeatures"), so this need not walk subfeatures itself;
 * doing so would double-count the same cell. Deduped: this backs verdict-filter matching, where
 * only membership matters, not count.
 */
export function featureVerdicts(vendor: ArtifactVendor, matrix: ComparisonMatrix, feature: ArtifactFeature): Verdict[] {
  const verdicts = new Set<Verdict>();
  for (const axis of feature.cited_by) {
    const cell = cellFor(matrix, axis, vendor.company);
    if (cell) verdicts.add(cell.verdict);
  }
  return [...verdicts];
}

/** Proposed/dropped entries carry no verdict — an active verdict filter excludes them (there's nothing to match), same as it excludes not-cited features. */
export function vendorListItemMatchesVerdicts(
  item: VendorListItem,
  vendor: ArtifactVendor,
  matrix: ComparisonMatrix,
  verdicts: Verdict[],
): boolean {
  if (!verdicts.length) return true;
  if (item.kind !== 'feature') return false;
  return featureVerdicts(vendor, matrix, item.feature).some((v) => verdicts.includes(v));
}

/** All four constraints, ANDed — the vendor pivot's single filtered list for one vendor. */
export function filterVendorItems(vendor: ArtifactVendor, matrix: ComparisonMatrix, filters: FilterState): VendorListItem[] {
  return vendorListItems(vendor).filter(
    (item) =>
      vendorListItemMatchesFacet(item, filters.facet) &&
      vendorListItemMatchesQuery(item, filters.query) &&
      vendorListItemMatchesVerdicts(item, vendor, matrix, filters.verdicts),
  );
}

/** Human-readable summary of the active filters, for empty-state messages ("nothing matches: <this>") — never a blank pane (spec). */
export function describeActiveFilters(filters: FilterState): string {
  const parts: string[] = [];
  if (filters.query.trim()) parts.push(`search "${filters.query.trim()}"`);
  if (filters.verdicts.length) parts.push(`verdict in [${filters.verdicts.join(', ')}]`);
  if (filters.vendors.length) parts.push(`vendor in [${filters.vendors.join(', ')}]`);
  if (filters.facet !== 'all') parts.push(`facet "${filters.facet}"`);
  return parts.length ? parts.join(', ') : 'no active filters';
}
